/**
 * Hook that enriches raw billing usage entries with human-readable
 * conversation names and user names from lookup sources.
 */

import { useMemo } from 'react'

import type { BillingUsageEntry } from '@/components/billing/billing.interface'
import { type UserLookupMap, useOrgMembersLookup } from '@/hooks/use-org-members-lookup.hook'

import {
  type ConversationNameMap,
  useConversationNamesLookup,
} from './use-conversation-names-lookup.hook'

const extractUniqueConversationIds = (entries: BillingUsageEntry[]): string[] => [
  ...new Set(entries.map((entry) => entry.chatId).filter(Boolean) as string[]),
]

const resolveUserName = (userId: string, userLookup: UserLookupMap): string | undefined =>
  userLookup.get(userId)?.name

const resolveChatName = (
  chatId: string | undefined,
  conversationNameMap: ConversationNameMap
): string | undefined => {
  if (!chatId) return undefined
  return conversationNameMap.get(chatId)
}

const enrichEntry = (
  entry: BillingUsageEntry,
  conversationNameMap: ConversationNameMap,
  userLookup: UserLookupMap
): BillingUsageEntry => ({
  ...entry,
  chatName: resolveChatName(entry.chatId, conversationNameMap) ?? entry.chatName,
  userName: resolveUserName(entry.userId, userLookup) ?? entry.userName,
})

const enrichUsageEntries = (
  entries: BillingUsageEntry[],
  conversationNameMap: ConversationNameMap,
  userLookup: UserLookupMap
): BillingUsageEntry[] =>
  entries.map((entry) => enrichEntry(entry, conversationNameMap, userLookup))

export const useEnrichedUsageData = (data: BillingUsageEntry[]): BillingUsageEntry[] => {
  const { userLookup } = useOrgMembersLookup()

  const conversationIds = useMemo(() => extractUniqueConversationIds(data), [data])

  const conversationNameMap = useConversationNamesLookup(conversationIds)

  return useMemo(
    () => enrichUsageEntries(data, conversationNameMap, userLookup),
    [data, conversationNameMap, userLookup]
  )
}
